import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Briefcase, MapPin, Clock, Send, Loader2, ArrowRight, Users, TrendingUp, Award, Upload, FileText, X, CheckCircle2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { fetchOpenings, submitLead, uploadResume, RESUME_MAX_BYTES, RESUME_ALLOWED_EXT } from "@/lib/api";
import { WAREHOUSE_IMAGE } from "@/lib/site-data";

const PERKS = [
    { icon: Users, t: "Family Culture", body: "A close-knit 40+ member team where ownership is celebrated over hierarchy." },
    { icon: TrendingUp, t: "Real Growth", body: "Career paths from operations to leadership — backed by two decades of stability." },
    { icon: Award, t: "Industry Exposure", body: "Work with India's biggest automotive and engineering brands every single day." },
];

function fmtSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function ResumeUploader({ onUploaded }) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [uploaded, setUploaded] = useState(null);

    const handleFile = async (file) => {
        if (!file) return;
        const ext = (file.name.split(".").pop() || "").toLowerCase();
        if (!RESUME_ALLOWED_EXT.includes(ext)) {
            toast.error(`Only ${RESUME_ALLOWED_EXT.join(", ").toUpperCase()} files are allowed.`);
            return;
        }
        if (file.size > RESUME_MAX_BYTES) {
            toast.error(`File too large. Max size is 5 MB. Yours: ${fmtSize(file.size)}.`);
            return;
        }
        setUploading(true);
        setProgress(0);
        try {
            const result = await uploadResume(file, setProgress);
            setUploaded({ ...result, originalName: file.name });
            onUploaded(result);
            toast.success("Resume uploaded.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not upload resume.");
        } finally {
            setUploading(false);
        }
    };

    const onSelect = (e) => handleFile(e.target.files?.[0]);
    const onDrop = (e) => {
        e.preventDefault();
        handleFile(e.dataTransfer.files?.[0]);
    };
    const onClear = () => {
        setUploaded(null);
        onUploaded({ url: "", filename: "" });
        if (inputRef.current) inputRef.current.value = "";
    };

    if (uploaded) {
        return (
            <div data-testid="resume-uploaded" className="border-2 border-emerald-600 bg-emerald-50 p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" strokeWidth={1.75} />
                    <div className="min-w-0">
                        <p className="text-xs uppercase tracking-widest font-bold text-emerald-700">Resume uploaded</p>
                        <p className="text-sm text-[#101A2C] truncate" title={uploaded.originalName || uploaded.filename}>
                            {uploaded.originalName || uploaded.filename}
                        </p>
                    </div>
                </div>
                <button type="button" onClick={onClear} data-testid="resume-clear" className="text-[10px] uppercase tracking-widest font-bold text-[#4B6390] hover:text-red-600 border-b border-current">
                    Replace
                </button>
            </div>
        );
    }

    return (
        <div
            data-testid="resume-uploader"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="border-2 border-dashed border-[#CBD5E1] hover:border-[#28406A] bg-[#F8FAFC] p-6 text-center cursor-pointer transition-colors"
            onClick={() => !uploading && inputRef.current?.click()}
        >
            <input
                ref={inputRef}
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={onSelect}
                data-testid="resume-file-input"
                className="hidden"
            />
            {uploading ? (
                <div>
                    <Loader2 className="h-7 w-7 mx-auto mb-3 text-[#28406A] animate-spin" strokeWidth={1.75} />
                    <p className="text-sm font-bold text-[#101A2C]">Uploading… {progress}%</p>
                    <div className="h-1.5 mt-3 bg-[#E9EEF3] overflow-hidden">
                        <div className="h-full bg-[#F4A000] transition-all" style={{ width: `${progress}%` }} />
                    </div>
                </div>
            ) : (
                <div>
                    <div className="w-12 h-12 mx-auto mb-3 inline-flex items-center justify-center bg-[#28406A] text-white">
                        <Upload className="h-5 w-5" strokeWidth={1.75} />
                    </div>
                    <p className="font-display uppercase text-base text-[#101A2C] mb-1">Drop your resume or click to browse</p>
                    <p className="text-xs text-[#4B6390]">
                        Accepted: <strong>PDF, DOC, DOCX</strong> · Max size: <strong>5 MB</strong>
                    </p>
                </div>
            )}
        </div>
    );
}

function CareerForm({ openings }) {
    const initial = { name: "", email: "", phone: "", experience: "", position: "", resume_url: "", message: "" };
    const [data, setData] = useState(initial);
    const [loading, setLoading] = useState(false);
    const onChange = (e) => setData((d) => ({ ...d, [e.target.name]: e.target.value }));

    const onResumeUploaded = (res) => {
        setData((d) => ({ ...d, resume_url: res.url || "" }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await submitLead("career", data);
            toast.success("Application received. Our HR team will reach out shortly.");
            setData(initial);
            // Force-clear the uploader by remounting via key change handled below
        } catch (err) {
            toast.error(err?.response?.data?.detail?.[0]?.msg || "Could not submit. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={onSubmit} data-testid="career-form" className="bg-white border border-[#CBD5E1] p-5 sm:p-7 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
                <label className="label-vl">Full Name *</label>
                <input data-testid="career-name" name="name" required value={data.name} onChange={onChange} className="input-vl" />
            </div>
            <div>
                <label className="label-vl">Email *</label>
                <input data-testid="career-email" type="email" name="email" required value={data.email} onChange={onChange} className="input-vl" />
            </div>
            <div>
                <label className="label-vl">Phone *</label>
                <input data-testid="career-phone" name="phone" required value={data.phone} onChange={onChange} className="input-vl" />
            </div>
            <div>
                <label className="label-vl">Experience (years) *</label>
                <input data-testid="career-experience" name="experience" required value={data.experience} onChange={onChange} className="input-vl" />
            </div>
            <div className="md:col-span-2">
                <label className="label-vl">Apply for Position</label>
                <select data-testid="career-position" name="position" value={data.position} onChange={onChange} className="input-vl">
                    <option value="">General application</option>
                    {openings.map((o) => (
                        <option key={o.id} value={o.title}>{o.title} — {o.location}</option>
                    ))}
                </select>
            </div>
            <div className="md:col-span-2">
                <label className="label-vl">Resume Upload</label>
                <ResumeUploader onUploaded={onResumeUploaded} />
                <input type="hidden" name="resume_url" value={data.resume_url || ""} readOnly data-testid="career-resume" />
            </div>
            <div className="md:col-span-2">
                <label className="label-vl">Cover note</label>
                <textarea data-testid="career-message" name="message" rows={4} value={data.message} onChange={onChange} className="input-vl" />
            </div>
            <div className="md:col-span-2">
                <button data-testid="career-submit" type="submit" disabled={loading} className="btn-primary disabled:opacity-60">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {loading ? "Submitting…" : "Submit Application"}
                </button>
            </div>
        </form>
    );
}

export default function Careers() {
    const [openings, setOpenings] = useState([]);

    useEffect(() => {
        fetchOpenings()
            .then(setOpenings)
            .catch(() => setOpenings([]));
    }, []);

    return (
        <div data-testid="page-careers">
            <PageHeader
                overline="Careers"
                title="Build the future of Indian logistics with us."
                subtitle="Join a 40+ member team that runs India's busiest manufacturing corridor — with the discipline of operations and the heart of a family business."
                image={WAREHOUSE_IMAGE}
                breadcrumb={[{ label: "Careers" }]}
                testId="careers-header"
            />

            {/* Why work with us */}
            <section className="bg-white py-20" data-testid="careers-why">
                <div className="container-vl">
                    <p className="overline mb-4">Why Vibhuti</p>
                    <h2 className="heading-h2 text-[#101A2C] mb-12 max-w-3xl">Two decades of stability. Industry-first culture.</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {PERKS.map((p, i) => (
                            <div key={p.t} className="bg-[#E9EEF3] p-7 border-l-4 border-[#F4A000]" data-testid={`perk-${i}`}>
                                <p.icon className="h-8 w-8 text-[#28406A] mb-5" strokeWidth={1.5} />
                                <h3 className="font-display uppercase text-xl text-[#101A2C] mb-2">{p.t}</h3>
                                <p className="text-sm text-[#4B6390] leading-relaxed">{p.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Current openings */}
            <section className="bg-[#F8FAFC] py-20 border-y border-[#CBD5E1]" data-testid="careers-openings">
                <div className="container-vl">
                    <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                        <div>
                            <p className="overline mb-4">Current Openings</p>
                            <h2 className="heading-h2 text-[#101A2C]">We're hiring across operations, fleet, warehousing & BD.</h2>
                        </div>
                        <p className="text-xs uppercase tracking-[0.2em] text-[#4B6390]">
                            {openings.length} open positions
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {openings.map((o) => (
                            <div key={o.id} className="bg-white border border-[#CBD5E1] p-7 hover:border-[#28406A] hover:shadow-[4px_4px_0_#28406A] transition-all" data-testid={`opening-${o.id}`}>
                                <div className="flex items-center gap-2 mb-3">
                                    <Briefcase className="h-4 w-4 text-[#F4A000]" strokeWidth={1.5} />
                                    <span className="text-xs uppercase tracking-[0.2em] text-[#4B6390]">{o.type}</span>
                                </div>
                                <h3 className="font-display uppercase text-xl text-[#101A2C] mb-3">{o.title}</h3>
                                <div className="flex flex-wrap gap-4 text-xs text-[#4B6390] mb-4">
                                    <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{o.location}</span>
                                    <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{o.experience}</span>
                                </div>
                                <p className="text-sm text-[#4B6390] mb-5">{o.description}</p>
                                <a
                                    href="#apply"
                                    data-testid={`apply-${o.id}`}
                                    className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#28406A] border-b-2 border-[#F4A000] pb-0.5"
                                >
                                    Apply Now <ArrowRight className="h-3 w-3" />
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Application form */}
            <section id="apply" className="bg-white py-20" data-testid="careers-form-section">
                <div className="container-vl grid grid-cols-12 gap-10">
                    <div className="col-span-12 lg:col-span-4">
                        <p className="overline mb-4">Apply</p>
                        <h2 className="heading-h2 text-[#101A2C] mb-6">Send us your application.</h2>
                        <p className="body-lg">Don't see your dream role? Drop us a note — we hire continuously for operations and field roles.</p>
                    </div>
                    <div className="col-span-12 lg:col-span-8">
                        <CareerForm openings={openings} />
                    </div>
                </div>
            </section>
        </div>
    );
}
